/*
 * Syariah compliance: one source for every page that mentions it.
 *
 * Source: two letters from Lariba Consulting (2927924-W), Tulus's Shariah
 * adviser, both dated 19 August 2026 (6 Rabiul Awal 1448) and signed by
 * Abd Aziz bin Mohd Nor, CEO. One covers the operations of the Tulus app,
 * the other the Tulus payment gateway. The PDFs are published as they were
 * received, in public/documents/.
 *
 * Wording (root AGENTS.md 14b): say what the adviser confirmed, about which
 * operations, and when, and link the letter. "Patuh kepada prinsip Syariah" / "in line with
 * Syariah principles" is the letters' own claim. The bare "patuh Syariah" and
 * "Syariah-compliant" stay banned by tests/pages.spec.ts; never "certified by" a
 * regulator, and never stretch it to institutions' zakat or waqf rulings,
 * which stay with each institution (see governance.ts).
 */
import type { Language } from "../components/useLanguage";

export const SYARIAH_ADVISER = "Lariba Consulting";
export const SYARIAH_DATE = "2026-08-19";

// Served from public/; prefixed with the base path where they are linked.
export const SYARIAH_DOCS = {
  app: "/documents/tulus-syariah-aplikasi-2026-08-19.pdf",
  gateway: "/documents/tulus-syariah-gerbang-pembayaran-2026-08-19.pdf",
};

const EN = {
  title: "Syariah compliance",
  intro: [
    "Lariba Consulting is the Syariah adviser to Tulus Digital Sdn Bhd. They have reviewed the contracts, operations and customer and user terms behind our services, made the amendments they needed, and confirmed that both the Tulus app and the Tulus payment gateway operate in line with Syariah principles.",
    "We keep that status under a Syariah Compliance Agreement with Lariba Consulting, which sets out the conditions we must continue to meet.",
  ],
  certificates: [
    { key: "app" as const, title: "Tulus app", body: "The operation of the Tulus app, including its contracts and its terms with customers and users." },
    { key: "gateway" as const, title: "Tulus payment gateway", body: "The operation of the Tulus payment gateway, including its contracts and its terms with customers and users." },
  ],
  confirmed: "Confirmed 19 August 2026 (6 Rabiul Awal 1448)",
  view: "View the Syariah Certificate (PDF, in Malay)",
  scope:
    "These Syariah Certificates cover how Tulus itself operates. Decisions on zakat, waqf and other programmes, including their Syariah governance, remain with the institution that runs them.",
  // The short version, for Home, Governance and the footer.
  short: "Syariah adviser Lariba Consulting has confirmed that the Tulus app and payment gateway operate in line with Syariah principles (19 August 2026).",
  shortLink: "Read about our Syariah compliance",
  footer: "Syariah compliance",
  // The Governance page: who is responsible for which Syariah matters.
  governance: {
    title: "Syariah governance",
    intro: "Syariah governance runs through every service on Tulus, and each party is responsible for its own part.",
    items: [
      {
        title: "Tulus's own operations",
        body: "Lariba Consulting, our Syariah adviser, has reviewed the contracts, operations and customer and user terms of the Tulus app and the Tulus payment gateway, and confirmed on 19 August 2026 that both operate in line with Syariah principles. We keep that status under a Syariah Compliance Agreement with Lariba Consulting.",
      },
      {
        title: "Zakat, waqf and other programmes",
        body: "Each institution decides the Syariah matters of its own programmes, including zakat and waqf requirements, eligibility and how funds are distributed, under its own Syariah governance. Tulus provides the digital service it has agreed to.",
      },
      {
        title: "Takaful and partner products",
        body: "Where a partner offers its own product through Tulus, such as takaful, that operator sets the product terms and is responsible for its Syariah requirements.",
      },
    ],
    certificates: "Syariah Certificates:",
    certificateLinks: { app: "Tulus app (PDF, in Malay)", gateway: "Tulus payment gateway (PDF, in Malay)" },
    more: "Read more on About",
  },
};

export type SyariahCopy = typeof EN;

const MS: SyariahCopy = {
  title: "Pematuhan Syariah",
  intro: [
    "Lariba Consulting ialah Penasihat Syariah kepada Tulus Digital Sdn Bhd. Mereka telah menyemak kontrak, operasi serta terma dengan pelanggan dan pengguna bagi perkhidmatan kami, membuat pindaan yang perlu, dan mengesahkan bahawa operasi aplikasi Tulus dan gerbang pembayaran Tulus patuh kepada prinsip Syariah.",
    "Status ini kami kekalkan melalui Perjanjian Pematuhan Syariah dengan Lariba Consulting, yang menetapkan syarat-syarat yang perlu terus kami penuhi.",
  ],
  certificates: [
    { key: "app" as const, title: "Aplikasi Tulus", body: "Operasi aplikasi Tulus, termasuk kontrak serta termanya dengan pelanggan dan pengguna." },
    { key: "gateway" as const, title: "Gerbang pembayaran Tulus", body: "Operasi gerbang pembayaran Tulus, termasuk kontrak serta termanya dengan pelanggan dan pengguna." },
  ],
  confirmed: "Disahkan 19 Ogos 2026 (6 Rabiul Awal 1448)",
  view: "Lihat Sijil Syariah (PDF)",
  scope:
    "Sijil Syariah ini merangkumi operasi Tulus sendiri. Keputusan berkaitan zakat, wakaf dan program lain, termasuk tadbir urus Syariahnya, kekal di bawah institusi yang mengendalikannya.",
  short: "Penasihat Syariah kami, Lariba Consulting, telah mengesahkan bahawa operasi aplikasi Tulus dan gerbang pembayaran Tulus patuh kepada prinsip Syariah (19 Ogos 2026).",
  shortLink: "Baca tentang pematuhan Syariah kami",
  footer: "Pematuhan Syariah",
  governance: {
    title: "Tadbir urus Syariah",
    intro: "Tadbir urus Syariah merentasi setiap perkhidmatan di Tulus, dan setiap pihak bertanggungjawab atas bahagiannya sendiri.",
    items: [
      {
        title: "Operasi Tulus sendiri",
        body: "Lariba Consulting, Penasihat Syariah kami, telah menyemak kontrak, operasi serta terma dengan pelanggan dan pengguna bagi aplikasi Tulus dan gerbang pembayaran Tulus, dan pada 19 Ogos 2026 mengesahkan bahawa kedua-duanya patuh kepada prinsip Syariah. Status ini kami kekalkan melalui Perjanjian Pematuhan Syariah dengan Lariba Consulting.",
      },
      {
        title: "Zakat, wakaf dan program lain",
        body: "Setiap institusi menentukan perkara Syariah bagi program masing-masing, termasuk keperluan zakat dan wakaf, kelayakan dan cara dana diagihkan, di bawah tadbir urus Syariahnya sendiri. Tulus menyediakan perkhidmatan digital yang dipersetujui.",
      },
      {
        title: "Takaful dan produk rakan",
        body: "Apabila rakan menawarkan produknya sendiri melalui Tulus, seperti takaful, pengendali tersebut menetapkan terma produk dan bertanggungjawab atas keperluan Syariahnya.",
      },
    ],
    certificates: "Sijil Syariah:",
    certificateLinks: { app: "Aplikasi Tulus (PDF)", gateway: "Gerbang pembayaran Tulus (PDF)" },
    more: "Baca lanjut di Tentang Kami",
  },
};

export const SYARIAH: Record<Language, SyariahCopy> = { en: EN, ms: MS };
